import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Check, Copy } from 'lucide-react'
// Prism core + just the grammars we use (explicit imports keep the bundle
// lean and load in dependency order: clike → js/ts, markup-templating → php).
import Prism from 'prismjs'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-markup-templating'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-csharp'
import 'prismjs/components/prism-java'

export interface CodeSample {
  /** Tab label, e.g. 'curl' or 'JavaScript'. */
  label: string
  /** Language hint shown in the chrome strip. */
  language: string
  /** Source string to render. */
  source: string
}

// Map our human language labels to Prism grammar keys. Tokens are themed in
// src/styles/index.css (the `.code-hl .token.*` block) using our CSS color
// vars, so highlighting tracks light/dark like the rest of the site.
const PRISM_LANG: Record<string, string> = {
  shell: 'bash', bash: 'bash', curl: 'bash',
  javascript: 'javascript', js: 'javascript',
  typescript: 'typescript', ts: 'typescript',
  json: 'json', python: 'python', php: 'php',
  csharp: 'csharp', 'c#': 'csharp', java: 'java',
}

function highlight(source: string, language: string): string | null {
  const key = PRISM_LANG[language.toLowerCase()]
  const grammar = key ? Prism.languages[key] : undefined
  if (!grammar) return null
  return Prism.highlight(source, grammar, key)
}

// ---- Shared language preference --------------------------------------------
// Picking a language (e.g. Python) in one code block switches every other
// block that offers it — a Python dev shouldn't have to re-pick on each
// endpoint. Backed by a tiny external store + localStorage so the choice also
// survives navigation. Blocks that don't have the chosen language just keep
// their own tab (handled in the component via index fallback).
const PREF_KEY = 'gb_code_lang'
let preferredLang: string | null = null
if (typeof window !== 'undefined') {
  try {
    preferredLang = window.localStorage.getItem(PREF_KEY)
  } catch {
    /* storage unavailable — fine, stays null */
  }
}
const prefListeners = new Set<() => void>()
function setPreferredLang(label: string) {
  preferredLang = label
  try {
    window.localStorage.setItem(PREF_KEY, label)
  } catch {
    /* ignore */
  }
  prefListeners.forEach((fn) => fn())
}
function subscribePref(cb: () => void) {
  prefListeners.add(cb)
  return () => {
    prefListeners.delete(cb)
  }
}
const getPrefSnapshot = () => preferredLang
const getPrefServerSnapshot = () => null

interface CodeBlockProps {
  title?: string
  samples: CodeSample[]
}

/**
 * Code block with optional tabs across the top and a copy button. Visually
 * matches the homepage ApiCta terminal block — three traffic-light dots in
 * the chrome strip, monospace body, color-token-backed for theme switching.
 */
export function CodeBlock({ title, samples }: CodeBlockProps) {
  // Local fallback pick (used when the shared language isn't offered here).
  const [localActive, setLocalActive] = useState(0)
  const [copied, setCopied] = useState(false)
  const preferred = useSyncExternalStore(subscribePref, getPrefSnapshot, getPrefServerSnapshot)
  // Honor the shared language when this block offers it; otherwise keep the
  // local pick (e.g. the response block only has `json`, the client block
  // only `TypeScript`).
  const preferredIndex = preferred ? samples.findIndex((s) => s.label === preferred) : -1
  const active = preferredIndex >= 0 ? preferredIndex : Math.min(localActive, samples.length - 1)
  const current = samples[active] ?? samples[0]
  const highlighted = highlight(current.source, current.language)

  // Code blocks scroll horizontally (wrapping would mangle code/JSON
  // structure). Track overflow so we can fade the edges that have more
  // content off-screen — the "there's more →" affordance so it doesn't read
  // as cut off.
  const preRef = useRef<HTMLPreElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })
  const updateEdges = useCallback(() => {
    const el = preRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setEdges({
      left: scrollLeft > 1,
      right: scrollLeft + clientWidth < scrollWidth - 1,
    })
  }, [])
  // Recompute when the visible sample changes (tab switch / new content).
  useLayoutEffect(updateEdges, [updateEdges, active, current.source])
  useEffect(() => {
    const el = preRef.current
    if (!el) return
    el.addEventListener('scroll', updateEdges, { passive: true })
    window.addEventListener('resize', updateEdges)
    return () => {
      el.removeEventListener('scroll', updateEdges)
      window.removeEventListener('resize', updateEdges)
    }
  }, [updateEdges])

  async function copy() {
    try {
      await navigator.clipboard.writeText(current.source)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — ignore */
    }
  }

  return (
    <div className="rounded-[var(--radius-lg)] overflow-hidden border border-[var(--color-border)] bg-[var(--color-elev)]">
      {/* chrome strip */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--color-border)]">
        <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-signal)]" aria-hidden />
        <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-accent)]" aria-hidden />
        <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-cream)]" aria-hidden />
        <span className="ml-3 font-[var(--font-mono)] text-[11px] text-[var(--color-muted)] uppercase tracking-[0.1em]">
          {title ?? current.language}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {samples.length > 1 && (
            <div role="tablist" aria-label="Language" className="flex items-center gap-0.5">
              {samples.map((s, i) => {
                const isActive = i === active
                return (
                  <button
                    key={s.label}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => {
                      setLocalActive(i)
                      setPreferredLang(s.label)
                    }}
                    className={[
                      'px-2 py-1 rounded-md text-[11px] font-[var(--font-mono)] uppercase tracking-[0.08em]',
                      'cursor-pointer transition-colors',
                      isActive
                        ? 'bg-[var(--code-tab-active-bg)] text-[var(--color-accent)]'
                        : 'text-[var(--color-muted)] hover:text-[var(--color-text)]',
                    ].join(' ')}
                  >
                    {s.label}
                  </button>
                )
              })}
            </div>
          )}
          <button
            type="button"
            onClick={copy}
            className={[
              'inline-flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer',
              'text-[11px] font-[var(--font-mono)] uppercase tracking-[0.08em]',
              'text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-elev)]',
            ].join(' ')}
            aria-label={copied ? 'Copied' : 'Copy'}
          >
            {copied ? <Check size={11} aria-hidden /> : <Copy size={11} aria-hidden />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>

      <div className="relative">
        <pre
          ref={preRef}
          // Horizontally scrollable, so it needs to be reachable by keyboard:
          // a region a mouse can scroll and a keyboard cannot is WCAG 2.1.1.
          // tabIndex makes it focusable and arrow-scrollable; role and label
          // stop a screen reader announcing a bare focusable with no purpose.
          tabIndex={0}
          role="region"
          aria-label={`${current.label} code sample`}
          className="code-scroll px-4 py-4 font-[var(--font-mono)] text-[12.5px] leading-relaxed text-[var(--color-text)] overflow-x-auto"
        >
          {highlighted ? (
            // Prism escapes the source into <span class="token …"> markup; the
            // input is our own static doc strings, so this is safe.
            <code className="code-hl" dangerouslySetInnerHTML={{ __html: highlighted }} />
          ) : (
            <code>{current.source}</code>
          )}
        </pre>
        {edges.left && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[var(--color-elev)] to-transparent"
          />
        )}
        {edges.right && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[var(--color-elev)] to-transparent"
          />
        )}
      </div>
    </div>
  )
}
